'use client'

import { Spacer, Text } from '@vinted/web-ui'
import { ReactNode, useCallback, useEffect, useState } from 'react'

import { businessAccountFromUserDto } from '@marketplace-web/domain/business-accounts-data'
import { useSession } from '@marketplace-web/shared/session'
import ProfileSideNavigation from 'components/ProfileSideNavigation'
import { getEprUinList } from 'data/api/get-epr-uin-list'
import { transformEprUinListDto } from 'data/transformers/epr'
import { EPR_GUIDE_URL } from 'constants/routes'
import { EprUinModel } from 'types/models/epr'
import { useTranslate } from '@marketplace-web/shared/i18n'

import AddCountryUinBlock from './AddCountryUinBlock'
import UinList from './UinList'

const EprSettings = () => {
  const [uinList, setUinList] = useState<Array<EprUinModel>>([])
  const [isUinListLoading, setIsUinListLoading] = useState(false)
  const [uinListError, setUinListError] = useState('')

  const fetchUinList = useCallback(async () => {
    setIsUinListLoading(true)
    const response = await getEprUinList()

    if ('errors' in response) {
      setIsUinListLoading(false)
      setUinListError(response.errors[0]!.value)

      return
    }

    setUinList(transformEprUinListDto(response))
    setIsUinListLoading(false)
  }, [])

  useEffect(() => {
    fetchUinList()
  }, [fetchUinList])

  const translate = useTranslate('epr_settings')
  const { user } = useSession()

  // Domestic EPR will always return 1 country, so set the country title to the first country title
  const countryTitle = uinList.length
    ? uinList[0]!.country_title
    : businessAccountFromUserDto(user)?.address?.country_title
  const allCategoriesSubmitted = uinList.length ? uinList[0]!.all_categories_submitted : false

  const eprLearnMoreLink = {
    'epr-learn-more-link': (chunks: Array<ReactNode>) => (
      <a
        href={EPR_GUIDE_URL}
        data-testid="epr-learn-more-link"
        target="_blank"
        rel="noopener noreferrer"
      >
        {chunks}
      </a>
    ),
  }

  return (
    <>
      <ProfileSideNavigation />
      <div className="body-content__content">
        <Spacer size={Spacer.Size.XLarge} />
        <Text as="h1" type={Text.Type.Heading}>
          {translate('title')}
        </Text>
        <Spacer size={Spacer.Size.Medium} />
        <Text as="span" html>
          {translate('subtitle', eprLearnMoreLink)}
        </Text>
        <Spacer size={Spacer.Size.X3Large} />
        <AddCountryUinBlock
          countryTitle={countryTitle || ''}
          disableAdd={allCategoriesSubmitted}
          refetch={fetchUinList}
        />
        {allCategoriesSubmitted && (
          <>
            <Spacer size={Spacer.Size.Medium} />
            <div className="all-categories-provided">
              <Text as="h3" type={Text.Type.Subtitle} theme="success">
                {translate('all_categories_provided')}
              </Text>
            </div>
          </>
        )}
        <Spacer size={Spacer.Size.XLarge} />
        {uinListError && (
          <Text as="h3" type={Text.Type.Subtitle}>
            {uinListError}
          </Text>
        )}
        {!uinListError && !!uinList.length && (
          <UinList
            uinList={uinList}
            countryTitle={countryTitle || ''}
            isListLoading={isUinListLoading}
            refetch={fetchUinList}
          />
        )}
      </div>
    </>
  )
}

export default EprSettings
